/**
 * cypAdhdObservationConfig.js — the Clinical Observation phase that can open
 * the CYP ADHD Semi-Structured Interview when an org's workflow document
 * stamps cypAdhdFlow.mse === 'observationInSsi'.
 *
 * SWITCH. This phase is one variant of cypAdhdFlow.mse. It renders when the
 * stamp is 'observationInSsi'. WLC and TOC stamp the other variant,
 * 'noObservationInSsi': the Clinical Interview keeps its Mental State
 * Examination (the child-specific questions) and this phase stays off.
 * Flip the org stamp to turn this phase on. No CYP ADHD patients exist yet.
 *
 * WHAT THIS IS. When this phase is on, the Clinical Interview has no Mental
 * State Examination. The original brief judged an MSE unhelpful here — it
 * covers appearance and posture, and it interrupts the appointment. In its
 * place: a short, light set of activities the clinician works through WITH
 * THE CHILD at the start of the appointment, taking free text notes on how
 * the child coped. The child then leaves and the rest of the appointment
 * (developmental history, the symptom walk) continues with the parent or
 * carer.
 *
 * Helen was explicit that this follows the shape of the adult autism
 * Behavioural Observation (tasks → notes) but is "nowhere near as involved
 * as an ADOS": two or three scenarios with a handful of prompts each. Four
 * tasks below, each a few lines. Resist growing them.
 *
 * TAGGING. Every note can be tagged to one or more of the 18 DSM-5 ADHD
 * symptoms (CYP_OBSERVATION_TAG_OPTIONS below). A note tagged A1c surfaces
 * again on the "Listening" symptom page later in the same interview, so the
 * clinician sees what they observed when deciding whether the symptom is
 * present. The tag INFORMS the decision; it never sets a symptom's status.
 * Helen does not want every criterion to need corroboration across school,
 * parent and observation — an overall sense is enough.
 *
 * CHILD NOT PRESENT. Attendance is not mandatory. If the child is not there,
 * or will not engage, the clinician marks each task "not administered" (or
 * simply moves on — nothing here gates the rest of the interview) and
 * completes the observation at a follow-up appointment. There is no separate
 * "child not present" flag: marking the tasks is the one way to say it.
 *
 * CONTENT STATUS. The prompts and probes below are a first draft from
 * Helen's spoken examples. They have NOT yet been through the clinical
 * (Tom) and comprehension (Jasmine) gates, and Helen has a document phrasing
 * the diagnostic criteria as child-friendly questions plus activity sheets
 * from earlier ADHD work, both still to be chased. TDI-182 (The Owl Centre's
 * sign-off) now covers THESE prompts. Expect wording to change; the shape
 * (id, prompts, probes) is what the page is built on.
 *
 * STORAGE. On the SSI stage document, alongside the symptoms:
 *
 *   data.observation.tasks.{taskId}
 *       notAdministered   bool
 *       promptsDone       [promptId]            prompts the clinician ticked
 *       masterNotes       structured-notes array; each note's criteriaTags
 *                         holds DSM codes ('A1a'..'A2i')
 *
 * Not seeded: the frontend creates the path on the first edit. Every reader
 * treats a missing data.observation as "nothing recorded yet".
 *
 * Task ids are STABLE storage keys — renaming one orphans recorded notes.
 * They are mirrored in python-functions/constants/cyp_adhd/ssi_data_model.py
 * (SSI_OBSERVATION_TASK_IDS) for the day the report pipeline reads them.
 */

import { CYP_SSI_SYMPTOMS, cypSsiSymptomNavPrefix } from './cypAdhdSsiConfig';

/** The phase's title, as the sidebar, breadcrumb and footer name it. */
export const CYP_ADHD_OBSERVATION_TITLE = 'Clinical Observation';

/**
 * Shown above the first task. Sets expectations for a clinician who has not
 * run one before: it is short, it is with the child, and skipping is fine.
 */
export const CYP_ADHD_OBSERVATION_INTRO = Object.freeze([
  'A short set of activities to work through with the child at the start of the appointment. Take notes on how they cope with each, then let them go and continue with the parent or carer.',
  'Tag a note to any symptoms it speaks to. Tagged notes appear again on those symptom pages later in this interview. Tags inform your decision; they do not make it.',
  'If the child is not here, or does not want to take part, mark the task "Not administered" and come back to it at a follow-up appointment. Nothing here blocks the rest of the interview.',
]);

/**
 * The tasks, in the order the clinician runs them. Light and conversational
 * by design — see the module comment.
 *
 *   id                  stable storage key (never rename)
 *   shortTitle          sidebar and footer label
 *   fullTitle           heading on the task panel
 *   focusOfObservation  one line on what the task is for, under the heading
 *   prompts             what to do, in order. Each has a tick box so the
 *                       clinician can see where they got to:
 *       id      stable within the task
 *       label   the instruction, written to the clinician
 *       probes  optional follow-up questions or things to watch for
 */
export const CYP_ADHD_OBSERVATION_TASKS = Object.freeze([
  {
    id: 'scavengerHunt',
    shortTitle: 'Scavenger hunt',
    fullTitle: 'Scavenger hunt',
    focusOfObservation:
      'Holding a short list of instructions in mind and carrying them out in order.',
    prompts: [
      {
        id: 'giveInstructions',
        label: 'Give three or four simple instructions in one go, then let the child carry them out. For example: "Find something red, bring me the blue pen, then put the book on the chair."',
        probes: [
          'How many did they complete, and in the right order?',
          'Did they ask for the list to be repeated, or start before you had finished?',
          'Did anything in the room pull them off task along the way?',
        ],
      },
      {
        id: 'addOneMore',
        label: 'If that was easy, go again with one more instruction added.',
        probes: [
          'Where did the sequence break down?',
          'Did they notice they had missed a step, or need telling?',
        ],
      },
    ],
  },
  {
    id: 'storyRecall',
    shortTitle: 'Short story',
    fullTitle: 'Short story, told back',
    focusOfObservation:
      'Listening and keeping attention while someone else is talking.',
    prompts: [
      {
        id: 'tellStory',
        label: 'Tell a short story — four or five sentences, with a couple of names and one thing that happens. Then ask the child to tell it back to you.',
        probes: [
          'Which details came back, and which were missing or invented?',
          'Did their attention drift partway through the telling?',
          'Did they interrupt, or finish the story for you?',
        ],
      },
      {
        id: 'askAboutStory',
        label: 'Ask one or two questions about the story: who was in it, what happened, how it ended.',
        probes: [
          'Could they answer without the story being repeated?',
        ],
      },
    ],
  },
  {
    id: 'morningRoutine',
    shortTitle: 'A typical morning',
    fullTitle: 'Walk me through a typical morning',
    focusOfObservation:
      'Organisation, following everyday instructions and keeping track of time, in the child\'s own words — and a chance to hear about school, home and what they enjoy.',
    prompts: [
      {
        id: 'walkThrough',
        label: '"Walk me through a normal school morning, from waking up to leaving the house."',
        probes: [
          'Who gets them up? Do they need reminding more than once?',
          'What usually gets forgotten, or has to be looked for?',
          'How do they know how much time is left?',
        ],
      },
      {
        id: 'followUpSteps',
        label: 'Follow up on specific steps: getting up, brushing teeth, getting dressed, finding things for school.',
        probes: [
          'Which do they do on their own, and which need a parent to step in?',
          'What happens when the routine changes — a different day, a trip, a late start?',
        ],
      },
      {
        id: 'schoolHomeStrengths',
        label: 'Light questions about school and home: what they like best, what they find hard, what they are good at, what they do for fun.',
        probes: [
          'Do they settle on one thing or jump between topics?',
          'What do they say they are good at? Note strengths as well as difficulties.',
        ],
      },
    ],
  },
  {
    id: 'boardGame',
    shortTitle: 'A game with distraction',
    fullTitle: 'A short game, with a distraction',
    focusOfObservation:
      'Taking turns, careless mistakes and acting without thinking while attention is being pulled elsewhere.',
    prompts: [
      {
        id: 'playGame',
        label: 'Play a short, simple game with clear turns — snap, a matching game, noughts and crosses.',
        probes: [
          'Do they wait for their turn, or take yours?',
          'Do they rush moves and make mistakes they would not make if they looked?',
          'Do they stay seated, or fidget and get up?',
        ],
      },
      {
        id: 'introduceDistraction',
        label: 'Partway through, introduce a mild distraction: say something to the parent, move something on the desk, drop a pen.',
        probes: [
          'Did they look away, and how long did it take to come back?',
          'Did they lose track of whose turn it was?',
          'Did mistakes go up once the distraction was there?',
        ],
      },
    ],
  },
]);

/**
 * The tag picker's options: the 18 DSM-5 ADHD symptoms, derived from the SSI
 * symptom contract so the id a note is tagged with is exactly the dsmCode the
 * symptom page later filters on. Labelled as the SSI sidebar labels them
 * ("A 1 Attention to Detail", "H/I 3 Restlessness") so a clinician reads one
 * name for one symptom across the phase boundary.
 *
 * Deliberately NOT the CYP source tags (parent/carer, child, school) used on
 * the symptom pages' notes. The observation has one source — the clinician
 * watching the child — so attribution is implied and the tags carry meaning
 * instead.
 */
export const CYP_OBSERVATION_TAG_OPTIONS = Object.freeze(
  CYP_SSI_SYMPTOMS.map((symptom) => ({
    id: symptom.dsmCode,
    // The picker shows the prefix and the name ("A 1 Attention to Detail").
    // The chip on the note shows only the prefix, the same short name the
    // symptom list uses ("A 1", "H/I 3"), so a note tagged to several
    // symptoms stays one line.
    label: `${cypSsiSymptomNavPrefix(symptom)} ${symptom.name}`,
    chipLabel: cypSsiSymptomNavPrefix(symptom),
  })),
);

/** Label lookup for rendering a stored tag id back as its symptom name. */
export const CYP_OBSERVATION_TAG_LABEL_BY_ID = Object.freeze(
  Object.fromEntries(CYP_OBSERVATION_TAG_OPTIONS.map((o) => [o.id, o.label])),
);

/**
 * True when a note carries any text — the sidebar's "something was recorded
 * here" signal. StructuredNotesEditor keeps a trailing empty note while the
 * clinician is typing, so an array with entries is not enough on its own.
 */
function hasNoteText(note) {
  return typeof note?.text === 'string' && note.text.trim().length > 0;
}

/**
 * The sidebar tick: a task is complete once the clinician has written at
 * least one note on it. A task marked not administered is never complete —
 * the row is greyed instead — because nothing was observed, whatever notes
 * may remain from before it was marked.
 *
 * Ticked prompts alone do not count: a prompt box says "I asked this", not
 * "I recorded what happened", and the notes are what the symptom pages read.
 */
export function isCypObservationTaskComplete(taskData) {
  if (!taskData || taskData.notAdministered) return false;
  const notes = Array.isArray(taskData.masterNotes) ? taskData.masterNotes : [];
  return notes.some(hasNoteText);
}

/**
 * The observation notes tagged to one symptom, for that symptom's page.
 *
 * Walks the tasks in config order so the list reads top to bottom as the
 * appointment ran. Notes on a task marked not administered are left out:
 * the clinician has said that task did not happen, so anything still stored
 * on it must not be presented as evidence. Empty notes are left out too.
 *
 * Returns [] for a missing or empty data.observation — the case for every
 * patient created before this feature, and for every WLC patient.
 *
 * @param {object|undefined} observationData  ssiStage.data.observation
 * @param {string} dsmCode                     e.g. 'A1c'
 * @returns {Array<{ noteId: string, text: string, taskId: string, taskTitle: string }>}
 */
export function collectCypObservationTaggedNotes(observationData, dsmCode) {
  const tasks = observationData?.tasks;
  if (!tasks || !dsmCode) return [];

  return CYP_ADHD_OBSERVATION_TASKS.flatMap((task) => {
    const taskData = tasks[task.id];
    if (!taskData || taskData.notAdministered) return [];
    const notes = Array.isArray(taskData.masterNotes) ? taskData.masterNotes : [];
    return notes
      .filter((note) => hasNoteText(note)
        && Array.isArray(note.criteriaTags)
        && note.criteriaTags.includes(dsmCode))
      .map((note) => ({
        noteId: note.id,
        text: note.text,
        taskId: task.id,
        taskTitle: task.shortTitle,
      }));
  });
}
